import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Trash2 } from "lucide-react";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

/** Admin → Subscribers: emails captured by the homepage welcome popup. */
export const SubscribersTab = () => {
  const [rows, setRows] = useState([]);
  const [split, setSplit] = useState({ with_phone: 0, email_only: 0 });

  const load = () => api.get("/admin/marketing/subscribers")
    .then(({ data }) => {
      setRows(data.subscribers || []);
      setSplit({ with_phone: data.with_phone || 0, email_only: data.email_only || 0 });
    })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/marketing/subscribers/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copyAll = async () => {
    await navigator.clipboard.writeText(rows.map((r) => r.email).join(", "));
    toast.success(`${rows.length} email(s) copied`);
  };

  return (
    <Card className="border-border" data-testid="subscribers-tab">
      <CardContent className="p-6 lg:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h3 className="font-display text-2xl flex items-center gap-2"><Mail size={18} /> Newsletter subscribers</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Emails left in the homepage welcome popup in exchange for the live promo code.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" data-testid="subscribers-count">{rows.length} total</Badge>
            <Badge variant="outline" data-testid="subscribers-split">
              {split.with_phone} with phone · {split.email_only} email only
            </Badge>
            {rows.length > 0 && (
              <Button variant="outline" onClick={copyAll} className="btn-pill border-border" data-testid="subscribers-copy">
                Copy all emails
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-3 pr-4 font-normal">Email</th>
                <th className="py-3 pr-4 font-normal">Phone</th>
                <th className="py-3 pr-4 font-normal">Joined</th>
                <th className="py-3 pr-4 font-normal">Code shown</th>
                <th className="py-3 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/70 hover:bg-muted/40 transition-colors" data-testid={`subscriber-row-${r.id}`}>
                  <td className="py-3 pr-4">{r.email}</td>
                  <td className="py-3 pr-4 whitespace-nowrap">{r.phone || "—"}</td>
                  <td className="py-3 pr-4 whitespace-nowrap">{day(r.created_at)}</td>
                  <td className="py-3 pr-4">{r.promo_code || "—"}</td>
                  <td className="py-3 text-right">
                    <button onClick={() => remove(r.id)} className="text-destructive" title="Remove" data-testid={`subscriber-del-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground py-6" data-testid="subscribers-empty">
              No signups yet. The popup only shows while an offer is live in Admin → Offers.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscribersTab;
